import { useQuery, useQueries, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest, fetchJson } from "@/lib/queryClient";
import type {
  Atividade,
  Setor,
  MnjProduto, InsertMnjProduto,
  MnjOperacaoWithItens,
  MnjOperacaoStatus,
  MnjOperacaoPrioridade,
  MnjLoteCommit,
  MnjLote,
  MnjProgramaLoteCommit,
} from "@shared/schema";
import { CONSULTA_FRESCA } from "@/lib/consulta-fresca";
import type { PropostaManejo } from "@shared/manejo-proposta";
import type { ResultadoDivergencia } from "@shared/manejo-divergencia";
import type { MnjPrograma } from "@shared/schema";

// Espelha o shape retornado por server/storage/manejo.ts (getPrograma/getProgramasBySetor)
// — não importamos direto do server aqui: client não deve depender de módulos do server.
export type ProgramaItemInput = { mnjProdutoId: string; kgPorHa: string; frequenciaDias?: number | null; ordem?: number };
export type MnjProgramaComItens = MnjPrograma & {
  itens: Array<{ id: string; mnjProdutoId: string; kgPorHa: string; frequenciaDias: number | null; ordem: number; produto: MnjProduto }>;
};

// ─── Setores (Sprint 1: setores é o canônico — ver server/routes/manejo/setores.ts) ──
// Criar/editar nome, área, data de plantio etc. é do Cacho (useCreateSetor/useUpdateSetor
// em use-setores.ts) — aqui só lista e alterna `fertirrigavel`, o único campo do Manejo.

export function useSetoresManejo(includeInativos = false) {
  const url = includeInativos ? "/api/manejo/setores?includeInativos=true" : "/api/manejo/setores";
  return useQuery<Setor[]>({ queryKey: ["/api/manejo/setores", includeInativos], queryFn: () => fetchJson<Setor[]>(url) });
}

export function useUpdateFertirrigavel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, fertirrigavel }: { id: string; fertirrigavel: boolean }) =>
      apiRequest("PATCH", `/api/manejo/setores/${id}/fertirrigavel`, { fertirrigavel }).then(r => r.json()),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/api/manejo/setores"] }),
  });
}

// ─── Atividades (catálogo do Equipe, lido pela rota do Manejo) ────────────────
// Leitura pura: cadastrar/editar atividade é da tela do Equipe. A rota é
// /api/manejo/atividades e não /api/equipe/atividades pra que a tela de Operações
// não dependa do módulo "equipe" estar ativo (ver server/routes/manejo/atividades.ts).

// A atividade traz `categoria` (migração 052) — é ela que decide o ícone do card,
// se o formulário pergunta volume de calda e se a operação pode existir sem setor.
// O antigo `aegroTipo` saiu com o conector.
export function useAtividadesManejo() {
  return useQuery<Atividade[]>({
    queryKey: ["/api/manejo/atividades"],
    queryFn: () => fetchJson<Atividade[]>("/api/manejo/atividades"),
  });
}

// ─── Produtos ─────────────────────────────────────────────────────────────────

export function useProdutosManejo(includeInativos = false) {
  const url = includeInativos ? "/api/manejo/produtos?includeInativos=true" : "/api/manejo/produtos";
  return useQuery<MnjProduto[]>({ queryKey: ["/api/manejo/produtos", includeInativos], queryFn: () => fetchJson<MnjProduto[]>(url) });
}

export function useCreateProdutoManejo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<InsertMnjProduto>) => apiRequest("POST", "/api/manejo/produtos", data).then(r => r.json()),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/api/manejo/produtos"] }),
  });
}

export function useUpdateProdutoManejo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<InsertMnjProduto> }) =>
      apiRequest("PATCH", `/api/manejo/produtos/${id}`, data).then(r => r.json()),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/api/manejo/produtos"] }),
  });
}

export function useAdicionarAliasProduto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, alias }: { id: string; alias: string }) =>
      apiRequest("POST", `/api/manejo/produtos/${id}/aliases`, { alias }).then(r => r.json()),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/api/manejo/produtos"] }),
  });
}

// ─── Programas (masterplan) ───────────────────────────────────────────────────

export function useProgramasPorSetor(setorId: string | null) {
  return useQuery<MnjProgramaComItens[]>({
    queryKey: ["/api/manejo/programas", setorId],
    queryFn: () => fetchJson<MnjProgramaComItens[]>(`/api/manejo/programas?setor=${setorId}`),
    enabled: !!setorId,
  });
}

// Programa ATIVO de cada setor selecionado — usado pelo pré-preenchimento da criação
// manual de operação (sugestão, nunca imposição: criar operação sem programa nenhum
// tem que continuar funcionando). Mesma URL/queryKey de useProgramasPorSetor, então
// os dois compartilham cache. `isError` é devolvido de propósito: erro de rede aqui
// não pode virar "nenhum setor tem programa" silenciosamente.
export function useProgramasAtivosPorSetores(setorIds: string[]) {
  const results = useQueries({
    queries: setorIds.map(setorId => ({
      queryKey: ["/api/manejo/programas", setorId],
      queryFn: () => fetchJson<MnjProgramaComItens[]>(`/api/manejo/programas?setor=${setorId}`),
    })),
  });

  return {
    porSetor: setorIds.map((setorId, i) => ({
      setorId,
      programa: (results[i]?.data ?? []).find(p => p.status === "ativo") ?? null,
    })),
    isLoading: results.some(r => r.isLoading),
    isError: results.some(r => r.isError),
  };
}

export function useCreatePrograma() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { setorId: string; nome: string; periodoInicio?: string | null; periodoFim?: string | null; itens: ProgramaItemInput[] }) =>
      apiRequest("POST", "/api/manejo/programas", data).then(r => r.json()),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/api/manejo/programas"] }),
  });
}

export function useReplaceProgramaItens() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, itens }: { id: string; itens: ProgramaItemInput[] }) =>
      apiRequest("PUT", `/api/manejo/programas/${id}/itens`, { itens }).then(r => r.json()),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/api/manejo/programas"] }),
  });
}

export function useUpdateProgramaStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: "ativo" | "arquivado" }) =>
      apiRequest("PATCH", `/api/manejo/programas/${id}/status`, { status }).then(r => r.json()),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/api/manejo/programas"] }),
  });
}

// Confirmação da capacidade manejo.definir_programa_em_lote (Sprint 3 §3.1) — o
// card de proposta manda o payload já resolvido (pendências respondidas), mesmo
// desenho de useCriarLoteManejo/POST /api/manejo/lotes.
export function useConfirmarProgramaEmLote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: MnjProgramaLoteCommit) =>
      apiRequest("POST", "/api/manejo/programas/confirmar-lote", data).then(r => r.json()) as Promise<{ programas: MnjProgramaComItens[] }>,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/api/manejo/programas"] }),
  });
}

// ─── Interpretador + Lotes ────────────────────────────────────────────────────

export function useInterpretarManejo() {
  return useMutation({
    mutationFn: (texto: string) =>
      apiRequest("POST", "/api/manejo/interpretar", { texto }).then(r => r.json()) as Promise<PropostaManejo>,
  });
}

export function useCriarLoteManejo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: MnjLoteCommit) =>
      apiRequest("POST", "/api/manejo/lotes", data).then(r => r.json()) as Promise<{ lote: MnjLote; operacoes: MnjOperacaoWithItens[] }>,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/api/manejo/operacoes"] }),
  });
}

// ─── Operações / Kanban (Sprint 1: mnj_card → mnj_operacao) ────────────────────

export function useOperacoesManejo(
  filters: { semana?: string; de?: string; ate?: string; status?: MnjOperacaoStatus; backlog?: boolean } = {},
) {
  const params = new URLSearchParams();
  if (filters.semana) params.set("semana", filters.semana);
  // `ate` sem `de` = tudo até aquele dia, SEM piso — é o que a tela Hoje usa para
  // que o atrasado de duas semanas atrás continue aparecendo (mesma regra do
  // boletim, PR #137). Operação de backlog (data nula) não satisfaz `<=` e fica
  // de fora por construção.
  if (filters.de) params.set("inicio", filters.de);
  if (filters.ate) params.set("fim", filters.ate);
  if (filters.status) params.set("status", filters.status);
  if (filters.backlog) params.set("backlog", "1");
  const qs = params.toString();
  return useQuery<MnjOperacaoWithItens[]>({
    queryKey: ["/api/manejo/operacoes", filters.semana ?? null, filters.de ?? null, filters.ate ?? null, filters.status ?? null, filters.backlog ?? false],
    queryFn: () => fetchJson<MnjOperacaoWithItens[]>(`/api/manejo/operacoes${qs ? `?${qs}` : ""}`),
    ...CONSULTA_FRESCA,
  });
}

export function useOperacaoManejo(id: string | null) {
  return useQuery<MnjOperacaoWithItens>({
    queryKey: ["/api/manejo/operacoes", id],
    queryFn: () => fetchJson<MnjOperacaoWithItens>(`/api/manejo/operacoes/${id}`),
    enabled: !!id,
    ...CONSULTA_FRESCA,
  });
}

// A transição carrega, quando precisa, a DATA (sair do backlog exige uma; ir pro
// backlog exige que ela não venha) e o MOTIVO (obrigatório em `cancelada`). O
// servidor devolve 409 com a frase quando o par não fecha — a tela mostra a frase,
// nunca adivinha.
export function useUpdateOperacaoStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status, dataPrevista, motivo }: {
      id: string;
      status: MnjOperacaoStatus;
      dataPrevista?: string | null;
      motivo?: string;
    }) =>
      apiRequest("PATCH", `/api/manejo/operacoes/${id}/status`, { status, dataPrevista, motivo }).then(r => r.json()),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/api/manejo/operacoes"] }),
  });
}

// ─── Verbos de estado (spec §7) ───────────────────────────────────────────────
//
// Um hook por AÇÃO, espelhando os endpoints. A tela do operador não fala
// "status = em_andamento": ela fala "Iniciar". Manter o vocabulário da ação até a
// borda é o que faz o código da tela ler como o botão que o Flávio aperta.

export function useIniciarOperacao() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiRequest("POST", `/api/manejo/operacoes/${id}/iniciar`, {}).then(r => r.json()),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/api/manejo/operacoes"] }),
  });
}

export interface ConcluirInput {
  id: string;
  itens?: Array<{ mnjProdutoId: string; qtdRealizada: string }>;
  executores?: string[];
  observacao?: string | null;
}

/** Resposta do /concluir: a operação, mais o selo de divergência quando há. */
export type OperacaoConcluida = MnjOperacaoWithItens & { divergencia: ResultadoDivergencia | null };

// UMA chamada. Antes seriam três (status, itens, executores) — no campo, com sinal
// ruim, a segunda falha e a operação fica concluída sem quantidade nem executor.
export function useConcluirOperacao() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...corpo }: ConcluirInput) =>
      apiRequest("POST", `/api/manejo/operacoes/${id}/concluir`, corpo).then(r => r.json()) as Promise<OperacaoConcluida>,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/api/manejo/operacoes"] }),
  });
}

/** Campos editáveis de um item (PATCH /operacoes/:id) — não mexe no ciclo de vida. */
export interface EditarOperacaoInput {
  id: string;
  prioridade?: MnjOperacaoPrioridade;
  /** `null` limpa o título (vale com atividade). */
  titulo?: string | null;
  local?: string | null;
  frenteId?: string | null;
  atribuidos?: string[];
  // Editar = criar: o que a tela de criação pede, a edição também manda.
  atividadeId?: string | null;
  observacao?: string | null;
  /** SUBSTITUI o conjunto de setores. */
  setores?: Array<{ setorId: string }>;
  /** SUBSTITUI os itens previstos. */
  itens?: Array<{ mnjProdutoId: string; qtdPrevista: number }>;
}

export function useEditarOperacao() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...campos }: EditarOperacaoInput) =>
      apiRequest("PATCH", `/api/manejo/operacoes/${id}`, campos).then(r => r.json()),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/api/manejo/operacoes"] }),
  });
}

export function useCancelarOperacao() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, motivo }: { id: string; motivo: string }) =>
      apiRequest("POST", `/api/manejo/operacoes/${id}/cancelar`, { motivo }).then(r => r.json()),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/api/manejo/operacoes"] }),
  });
}

export function useReabrirOperacao() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiRequest("POST", `/api/manejo/operacoes/${id}/reabrir`, {}).then(r => r.json()),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/api/manejo/operacoes"] }),
  });
}

export function useComentarOperacao() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, texto }: { id: string; texto: string }) =>
      apiRequest("POST", `/api/manejo/operacoes/${id}/comentarios`, { texto }).then(r => r.json()),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/api/manejo/operacoes"] }),
  });
}

export function useUpdateOperacaoItensRealizado() {
  const qc = useQueryClient();
  return useMutation({
    // `qtdRealizada` na unidade do produto — não mais "kg" presumido.
    mutationFn: ({ id, itens }: { id: string; itens: Array<{ mnjProdutoId: string; qtdRealizada: string }> }) =>
      apiRequest("PATCH", `/api/manejo/operacoes/${id}/itens`, { itens }).then(r => r.json()),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/api/manejo/operacoes"] }),
  });
}

export function useExcluirOperacao() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/manejo/operacoes/${id}`).then(r => r.json()),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/api/manejo/operacoes"] }),
  });
}

// ─── Executores (Sprint 1 §1.2d) — N:N, sem efeito financeiro ──────────────────

export function useAddExecutorOperacao() {
  const qc = useQueryClient();
  return useMutation({
    // Sem `papel`, o servidor grava `atribuido` — acrescentar alguém a uma operação
    // é planejamento; `executou` é escrito pela conclusão (ciclo 2).
    mutationFn: ({ operacaoId, funcionarioId }: { operacaoId: string; funcionarioId: string }) =>
      apiRequest("POST", `/api/manejo/operacoes/${operacaoId}/executores`, { funcionarioId }).then(r => r.json()),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/api/manejo/operacoes"] }),
  });
}

export function useRemoveExecutorOperacao() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ operacaoId, funcionarioId }: { operacaoId: string; funcionarioId: string }) =>
      apiRequest("DELETE", `/api/manejo/operacoes/${operacaoId}/executores/${funcionarioId}`).then(r => r.json()),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/api/manejo/operacoes"] }),
  });
}

// ─── "Separar setor desta operação" (Sprint 1 §1.6) ────────────────────────────

export function useSepararSetorOperacao() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ operacaoId, setorId }: { operacaoId: string; setorId: string }) =>
      apiRequest("POST", `/api/manejo/operacoes/${operacaoId}/separar-setor`, { setorId }).then(r => r.json()) as Promise<{
        operacaoOriginal: MnjOperacaoWithItens;
        novaOperacao: MnjOperacaoWithItens;
      }>,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/api/manejo/operacoes"] }),
  });
}


// ─── Frentes de trabalho e Lista de pendências (migração 079) ─────────────────

export interface FrenteManejo {
  id: string;
  nome: string;
  emoji: string | null;
  responsavelFuncionarioId: string | null;
  encarregadoFuncionarioId: string | null;
  agentAudienciaId: string | null;
  lembreteAtivo: boolean;
  ordem: number;
  ativo: boolean;
  responsavelNome: string | null;
  encarregadoNome: string | null;
  audienciaNome: string | null;
}

export function useFrentesManejo(todas = false) {
  return useQuery<FrenteManejo[]>({
    queryKey: ["/api/manejo/frentes", todas],
    queryFn: () => fetchJson<FrenteManejo[]>(`/api/manejo/frentes${todas ? "?todas=1" : ""}`),
  });
}

export interface FrenteInput {
  nome?: string;
  emoji?: string | null;
  responsavelFuncionarioId?: string | null;
  encarregadoFuncionarioId?: string | null;
  agentAudienciaId?: string | null;
  lembreteAtivo?: boolean;
  ordem?: number;
  ativo?: boolean;
}

export function useSalvarFrente() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...dados }: FrenteInput & { id?: string }) =>
      (id
        ? apiRequest("PATCH", `/api/manejo/frentes/${id}`, dados)
        : apiRequest("POST", "/api/manejo/frentes", dados)
      ).then(r => r.json()) as Promise<FrenteManejo>,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/manejo/frentes"] });
      qc.invalidateQueries({ queryKey: ["/api/manejo/operacoes"] });
    },
  });
}

/** Grupos/contatos do Agente que a frente pode usar — liberado a quem configura frentes. */
export interface AudienciaDaFrente { id: string; nome: string; tipo: string }
export function useAudienciasDaFrente(enabled = true) {
  return useQuery<AudienciaDaFrente[]>({
    queryKey: ["/api/manejo/frentes/audiencias"],
    queryFn: () => fetchJson<AudienciaDaFrente[]>("/api/manejo/frentes/audiencias"),
    enabled,
  });
}

export interface ItemListaView {
  id: string;
  titulo: string | null;
  tituloExibicao: string;
  local: string | null;
  status: MnjOperacaoStatus;
  dataPrevista: string | null;
  atrasada: boolean;
  desde: string | null;
  prioridade: MnjOperacaoPrioridade;
  atribuidos: { funcionarioId: string; nome: string }[];
  concluidoEm: string | null;
  frenteId: string | null;
  ordem: number | null;
  comInsumos: boolean;
  /** Concluído por mim há ≤ 2 h: aparece o "Desfazer" (sem `manejo.reabrir`). */
  desfazivelPorMim: boolean;
  /** Instruções de quem planejou — 2 linhas no cartão. */
  observacao: string | null;
}

export interface GrupoListaView {
  frenteId: string | null;
  nome: string;
  emoji: string | null;
  itens: ItemListaView[];
  feitos: ItemListaView[];
}

export function useListaPendencias() {
  return useQuery<{ grupos: GrupoListaView[] }>({
    queryKey: ["/api/manejo/operacoes", "lista"],
    queryFn: () => fetchJson<{ grupos: GrupoListaView[] }>("/api/manejo/operacoes?vista=lista"),
    ...CONSULTA_FRESCA,
  });
}

export function useCriarItemLista() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dados: {
      titulo: string;
      frenteId: string | null;
      local?: string | null;
      prioridade?: MnjOperacaoPrioridade;
      dataPrevista?: string | null;
      atribuidos?: string[];
      observacao?: string | null;
    }) =>
      apiRequest("POST", "/api/manejo/operacoes", dados).then(r => r.json()),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/api/manejo/operacoes"] }),
  });
}

export function useCriarItensEmMassa() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dados: { frenteId: string; titulos: string[] }) =>
      apiRequest("POST", "/api/manejo/operacoes/em-massa", dados).then(r => r.json()) as Promise<{ criadas: number }>,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/api/manejo/operacoes"] }),
  });
}

export function useReordenarItens() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dados: { frenteId: string; ids: string[] }) =>
      apiRequest("PATCH", "/api/manejo/operacoes/ordem", dados).then(r => r.json()),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/api/manejo/operacoes"] }),
  });
}

export function useLocaisManejo() {
  return useQuery<string[]>({
    queryKey: ["/api/manejo/locais"],
    queryFn: () => fetchJson<string[]>("/api/manejo/locais"),
  });
}

export interface PreviaLembrete {
  data: string;
  texto: string;
  vazio: boolean;
  fazendo: number;
  naFila: number;
  feito: number;
  ultimoEnvio: { em: string; status: string; erro: string | null; manual: boolean } | null;
}

/** Prévia do lembrete de hoje — só monta o texto, não envia nada. */
export function usePreviaLembrete(frenteId: string | null, habilitada: boolean) {
  return useQuery<PreviaLembrete>({
    queryKey: ["/api/manejo/frentes", frenteId, "lembrete", "previa"],
    queryFn: () => fetchJson<PreviaLembrete>(`/api/manejo/frentes/${frenteId}/lembrete/previa`),
    enabled: !!frenteId && habilitada,
  });
}

export function useEnviarLembreteAgora() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (frenteId: string) =>
      apiRequest("POST", `/api/manejo/frentes/${frenteId}/lembrete/enviar`, {}).then(r => r.json()) as Promise<{ desfecho: string }>,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/api/manejo/frentes"] }),
  });
}

export type SecaoMinhasView = "fazendo" | "hoje" | "fila" | "depois";

export interface ItemMinhaView extends ItemListaView {
  frenteNome: string | null;
  frenteEmoji: string | null;
  secao: SecaoMinhasView;
}

export interface MinhasView {
  itens: ItemMinhaView[];
  feitosHoje: ItemMinhaView[];
  semVinculo: boolean;
}

/** O que está atribuído ao funcionário do usuário, em todas as frentes. */
export function useMinhasOperacoes() {
  return useQuery<MinhasView>({
    queryKey: ["/api/manejo/operacoes", "minhas"],
    queryFn: () => fetchJson<MinhasView>("/api/manejo/operacoes?vista=minhas"),
    ...CONSULTA_FRESCA,
  });
}
